import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Nav from '../Nav/Nav';
import SideNavList from './SideNavList/SideNavList';
import verdLogo from '../../images/verd_logo_transparent_black.png';
import taskService from '../../services/TaskService';
import { socket } from '../../socket';

import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import employeeQualificationService from '../../services/EmployeeQualificationService';
import { getQualificationStatus } from '../Qualification/helper/helper';

function cn(...inputs) {
  return twMerge(clsx(inputs));
}

function SideNav({ isCollapsed, mobileOpen, handleDrawerToggle, handleAppTitle }) {
  const [role, setRole] = useState();

  const [selectedItem, setSelectedItem] = useState('');
  const [myTaskNotificationCount, setMyTaskNotificationCount] = useState(0);
  const [qualificationNotificationCount, setQualificationNotificationCount] = useState(0);
  const [expandNestedItem, setExpandNestedItem] = useState({
    asset: false,
    timesheetAndLogs: false,
    message: false,
    invoices: false
  });

  useEffect(() => {
    // eslint-disable-next-line no-undef
    setRole(localStorage.getItem('role'));
  }, []);

  useEffect(() => {
    let mounted = true;
    const refreshTaskNotifications = async () => {
      try {
        const response = await taskService.getMyTask();
        // eslint-disable-next-line no-undef
        const userId = localStorage.getItem('userId');
        const tasks = response?.data?.data || [];
        const unread = tasks.filter(
          (task) =>
            (task.status === 'Assigned' || task.status === 'Pending' || task.status === 'On Hold') &&
            !(task.viewedBy || []).some((worker) => String(worker?._id || worker) === String(userId))
        ).length;
        if (mounted) setMyTaskNotificationCount(unread);
      } catch (error) {
        // The navigation should remain usable if task loading fails.
      }
    };

    refreshTaskNotifications();
    // eslint-disable-next-line no-undef
    window.addEventListener('taskViewed', refreshTaskNotifications);
    socket.on('newTaskAssignment', refreshTaskNotifications);
    return () => {
      mounted = false;
      // eslint-disable-next-line no-undef
      window.removeEventListener('taskViewed', refreshTaskNotifications);
      socket.off('newTaskAssignment', refreshTaskNotifications);
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    const refreshQualificationNotification = async () => {
      try {
        const response = await employeeQualificationService.fetchEmployeesQualifications();
        const qualificationRecords = response.data?.data || [];

        const unread = qualificationRecords.filter((quals) => {
          const status = getQualificationStatus(
            quals.status,
            quals.expiryDate,
            quals.issueDate,
            quals.referenceNumber
          ).toLowerCase();
          const isMandatory = (quals.mandatoryQualifications || []).includes(quals.name);

          return (
            quals.portalAccess === true &&
            (status === 'pending' || (isMandatory && (status === 'expired' || status === 'not submitted')))
          );
        }).length;

        if (mounted) setQualificationNotificationCount((current) => Math.max(current, unread));
      } catch (error) {
        //handle error if needed
      }
    };
    const handleQualificationApprovalRequest = () => {
      if (mounted) setQualificationNotificationCount((current) => current + 1);
      refreshQualificationNotification();
    };
    refreshQualificationNotification();

    // eslint-disable-next-line no-undef
    window.addEventListener('qualificationPending', refreshQualificationNotification);
    socket.on('qualificationApprovalRequest', handleQualificationApprovalRequest);

    return () => {
      mounted = false;
      // eslint-disable-next-line no-undef
      window.removeEventListener('qualificationPending', refreshQualificationNotification);
      socket.off('qualificationApprovalRequest', handleQualificationApprovalRequest);
    };
  }, []);

  const handleListItemClick = (value) => {
    setSelectedItem(value);
    if (!isCollapsed) {
      if (value === 'Asset') {
        setExpandNestedItem({ ...expandNestedItem, asset: !expandNestedItem.asset });
      }
      if (value === 'Timesheets and Logs') {
        setExpandNestedItem({ ...expandNestedItem, timesheetAndLogs: !expandNestedItem.timesheetAndLogs });
      }
      if (value === 'Message') {
        setExpandNestedItem({ ...expandNestedItem, message: !expandNestedItem.message });
      }
      if (value === 'Invoices') {
        setExpandNestedItem({ ...expandNestedItem, invoices: !expandNestedItem.invoices });
      }
    }
  };

  const handleLargeDevice = (value) => {
    setSelectedItem(value);
    if (!isCollapsed) {
      if (value === 'Asset') {
        setExpandNestedItem({ ...expandNestedItem, asset: !expandNestedItem.asset });
      }
      if (value === 'Timesheets and Logs') {
        setExpandNestedItem({ ...expandNestedItem, timesheetAndLogs: !expandNestedItem.timesheetAndLogs });
      }
      if (value === 'Message') {
        setExpandNestedItem({ ...expandNestedItem, message: !expandNestedItem.message });
      }
      if (value === 'Invoices') {
        setExpandNestedItem({ ...expandNestedItem, invoices: !expandNestedItem.invoices });
      }
    }
  };

  return (
    <>
      <Nav handleDrawerToggle={handleDrawerToggle} isCollapsed={isCollapsed} />

      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="tw-fixed tw-inset-0 tw-z-[500] tw-bg-black/40 sm:tw-hidden tw-backdrop-blur-sm tw-transition-opacity"
          onClick={handleDrawerToggle}
        />
      )}

      {/* Sidebar Aside */}
      <aside
        className={cn(
          'tw-fixed tw-top-0 tw-left-0 tw-z-[500] tw-h-screen tw-transition-all tw-duration-300 tw-ease-in-out tw-bg-white tw-border-r tw-border-gray-100 tw-flex tw-flex-col tw-overflow-y-auto tw-overflow-x-hidden',
          isCollapsed ? 'tw-w-16' : 'tw-w-64',
          mobileOpen ? 'tw-translate-x-0' : '-tw-translate-x-full sm:tw-translate-x-0'
        )}
        aria-label="Sidebar"
      >
        <div className={cn('tw-flex tw-flex-col tw-h-full tw-py-7', isCollapsed ? 'tw-px-4' : 'tw-px-6')}>
          {/* Logo Section */}
          <div className="tw-flex tw-items-center tw-space-x-3 tw-cursor-pointer" onClick={handleAppTitle}>
            <div className="tw-flex tw-items-center tw-justify-center">
              <img src={verdLogo} alt="website_logo" className="tw-w-24 tw-h-24 tw-object-contain" />
            </div>
            {/* {!isCollapsed && <span className="tw-text-xl tw-font-bold tw-text-gray-900 tw-tracking-tight">Verd</span>} */}
          </div>

          <div className="-tw-mt-2 tw-flex-1">
            {!isCollapsed && (
              <h3 className="tw-px-3 tw-text-[10px] tw-font-bold tw-text-gray-400 tw-uppercase tw-tracking-[0.2em] tw-mb-4">
                Menu
              </h3>
            )}

            <SideNavList
              isCollapsed={isCollapsed}
              mobileOpen={mobileOpen}
              handleDrawerToggle={handleDrawerToggle}
              role="User"
              myTaskNotificationCount={myTaskNotificationCount}
              selectedItem={selectedItem}
              handleListItemClick={handleListItemClick}
              handleLargeDevice={handleLargeDevice}
              expandNestedItem={expandNestedItem}
            />

            {role && role === 'Admin' && (
              <div className="tw-mt-10">
                {!isCollapsed && (
                  <h3 className="tw-px-3 tw-text-[10px] tw-font-bold tw-text-gray-400 tw-uppercase tw-tracking-[0.2em] tw-mb-4">
                    Administration
                  </h3>
                )}
                <SideNavList
                  isCollapsed={isCollapsed}
                  mobileOpen={mobileOpen}
                  handleDrawerToggle={handleDrawerToggle}
                  role="Admin"
                  myTaskNotificationCount={myTaskNotificationCount}
                  selectedItem={selectedItem}
                  handleListItemClick={handleListItemClick}
                  handleLargeDevice={handleLargeDevice}
                  expandNestedItem={expandNestedItem}
                  qualificationNotificationCount={qualificationNotificationCount}
                />
              </div>
            )}
          </div>
        </div>
      </aside>
    </>
  );
}

SideNav.propTypes = {
  window: PropTypes.func,
  isCollapsed: PropTypes.any,
  mobileOpen: PropTypes.any,
  handleDrawerToggle: PropTypes.any,
  handleAppTitle: PropTypes.any
};

export default SideNav;
